<template>
  <div class="working-hours">
    <!-- Banner Section -->
    <Banner
      image="https://images.unsplash.com/photo-1549639557-c5fb5b5ae708?q=80&w=2070"
      title="Working Hours"
      height="700px"
    />

    <!-- Wavy Divider -->
    <div class="wave-container">
      <svg class="wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 400" preserveAspectRatio="none">
        <path class="wave-path" d="M0,96L40,106.7C80,117,160,139,240,154.7C320,171,400,181,480,165.3C560,149,640,107,720,90.7C800,75,880,85,960,112C1040,139,1120,181,1200,181.3C1280,181,1360,139,1400,117.3L1440,96L1440,400L1400,400C1360,400,1280,400,1200,400C1120,400,1040,400,960,400C880,400,800,400,720,400C640,400,560,400,480,400C400,400,320,400,240,400C160,400,80,400,40,400L0,400Z"></path>
      </svg>
    </div>

    <div class="container">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading working hours...</p>
      </div>

      <template v-else>
        <!-- Current Status Card -->
        <div class="status-card" :class="{ open: isCurrentlyOpen }">
          <div class="status-icon">
            <i :class="isCurrentlyOpen ? 'fas fa-door-open' : 'fas fa-door-closed'"></i>
          </div>
          <div class="status-content">
            <h2>{{ isCurrentlyOpen ? 'We\'re Open!' : 'Currently Closed' }}</h2>
            <p v-if="isCurrentlyOpen">Open until {{ getCurrentClosingTime }}</p>
            <p v-else>{{ getNextOpeningText }}</p>
          </div>
        </div>

        <!-- Schedule Grid -->
        <div class="schedule-grid">
          <div v-for="schedule in workingHours" 
               :key="schedule.day" 
               class="schedule-card"
               :class="{ 'current-day': isCurrentDay(schedule.day) }"
               :style="{ borderColor: schedule.titleColor }">
            <div class="day-name" :style="{ color: schedule.titleColor }">
              {{ schedule.day }}
            </div>
            <div class="time" :class="{ closed: schedule.time === 'Closed' }">
              {{ schedule.time }}
            </div>
          </div>
        </div>

        <!-- Important Notes -->
        <div class="notes-section">
          <h3>Important Notes</h3>
          <ul>
            <li>
              <i class="fas fa-info-circle"></i>
              Last entry is 1 hour before closing time
            </li>
            <li>
              <i class="fas fa-calendar-alt"></i>
              Hours may vary during Ramadan and public holidays
            </li>
            <li>
              <i class="fas fa-exclamation-triangle"></i>
              The park may close during severe weather conditions
            </li>
          </ul>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import Banner from "@/components/Banner.vue";
import axiosInstance from '@/plugins/axios';

const workingHours = ref([]);
const loading = ref(true);

const fetchWorkingHours = async () => {
  try {
    const response = await axiosInstance.get('/api/setting/working-hours');
    workingHours.value = response.data;
  } catch (err) {
    console.error('Error fetching working hours:', err);
  } finally {
    loading.value = false;
  }
};

const getCurrentDay = () => {
  return new Date().toLocaleString('en-US', { weekday: 'long' });
};

const isCurrentDay = (day) => {
  return day === getCurrentDay();
};

const isCurrentlyOpen = computed(() => {
  if (!workingHours.value.length) return false;
  
  const currentDay = getCurrentDay();
  const currentSchedule = workingHours.value.find(schedule => schedule.day === currentDay);
  
  if (!currentSchedule || currentSchedule.time === 'Closed') {
    return false;
  }

  const now = new Date();
  const currentTime = now.getHours() * 100 + now.getMinutes();
  
  const [startStr, endStr] = currentSchedule.time.split(' - ');
  const [startHours, startMinutes] = startStr.match(/(\d+):(\d+)/).slice(1);
  const [endHours, endMinutes] = endStr.match(/(\d+):(\d+)/).slice(1);
  
  let startTime = parseInt(startHours);
  if (startStr.includes('PM') && startTime !== 12) startTime += 12;
  startTime = startTime * 100 + parseInt(startMinutes);
  
  let endTime = parseInt(endHours);
  if (endStr.includes('PM') && endTime !== 12) endTime += 12;
  endTime = endTime * 100 + parseInt(endMinutes);
  
  return currentTime >= startTime && currentTime < endTime;
});

const getCurrentClosingTime = computed(() => {
  const currentDay = getCurrentDay();
  const currentSchedule = workingHours.value.find(schedule => schedule.day === currentDay);
  if (currentSchedule && currentSchedule.time !== 'Closed') {
    return currentSchedule.time.split(' - ')[1];
  }
  return '';
});

const getNextOpeningText = computed(() => {
  const currentDayIndex = workingHours.value.findIndex(schedule => schedule.day === getCurrentDay());
  let nextOpenDay = null;
  let daysChecked = 0;
  
  while (daysChecked < 7) {
    const index = (currentDayIndex + daysChecked) % 7;
    if (workingHours.value[index].time !== 'Closed') {
      nextOpenDay = workingHours.value[index];
      break;
    }
    daysChecked++;
  }

  if (nextOpenDay) {
    if (nextOpenDay.day === getCurrentDay()) {
      return `Opens today at ${nextOpenDay.time.split(' - ')[0]}`;
    }
    return `Opens ${nextOpenDay.day} at ${nextOpenDay.time.split(' - ')[0]}`;
  }
  
  return 'Please check back later';
});

onMounted(() => {
  fetchWorkingHours();
});
</script>

<style scoped>
.working-hours {
  position: relative;
  background: white;
  margin-top: 700px;
}

.wave-container {
  position: absolute;
  top: -200px;
  left: 0;
  width: 100%;
  height: 220px;
  overflow: hidden;
}

.wave-top {
  position: absolute;
  bottom: -20px;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: bottom;
  animation: waveFloat 5s ease-in-out infinite alternate;
}

.wave-path {
  fill: #ffffff;
  filter: drop-shadow(0px -2px 2px rgba(0, 0, 0, 0.1));
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1268B1;
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 1s linear infinite;
}

/* Status Card */
.status-card {
  display: flex;
  align-items: center;
  gap: 20px;
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
  margin-bottom: 40px;
  border-left: 5px solid #dc3545;
}

.status-card.open {
  border-left-color: #28a745;
}

.status-icon {
  font-size: 2.5rem;
  color: #dc3545;
}

.status-card.open .status-icon {
  color: #28a745;
}

.status-content h2 {
  margin: 0;
  font-size: 1.8rem;
}

.status-content p {
  margin: 5px 0 0;
  color: #666;
}

/* Schedule Grid */
.schedule-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.schedule-card {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid;
  transition: transform 0.3s ease;
}

.schedule-card:hover {
  transform: translateY(-5px);
}

.schedule-card.current-day {
  background: #f8f9fa;
  transform: scale(1.02);
}

.day-name {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.time {
  color: #666;
}

.time.closed {
  color: #dc3545;
  font-style: italic;
}

/* Notes Section */
.notes-section {
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
}

.notes-section h3 {
  color: #1268B1;
  margin-bottom: 20px;
}

.notes-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.notes-section li {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  color: #666;
}

.notes-section li i {
  color: #1268B1;
}

@keyframes waveFloat {
  0% {
    transform: translateY(0) scaleY(1.02);
  }
  100% {
    transform: translateY(-10px) scaleY(1.05);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive Design */
@media (max-width: 768px) {
  .container {
    padding: 20px;
  }

  .status-card {
    padding: 20px;
  }

  .schedule-card {
    margin: 10px 0;
  }

  .wave-container {
    top: -150px;
    height: 170px;
  }
}

@media (max-width: 480px) {
  .wave-container {
    top: -100px;
    height: 120px;
  }

  .status-card {
    flex-direction: column;
    text-align: center;
  }
}

/* RTL Support */
[dir="rtl"] .status-card {
  border-left: none;
  border-right: 5px solid #dc3545;
}

[dir="rtl"] .status-card.open {
  border-right-color: #28a745;
}

[dir="rtl"] .schedule-card {
  border-left: none;
  border-right: 4px solid;
}

[dir="rtl"] .notes-section li {
  flex-direction: row-reverse;
}

[dir="rtl"] .wave-top {
  transform: scaleX(-1);
}
</style>